import { CrmTaskStatus } from '@plato/data/prisma';
import { lexicalToPlainText } from '@plato/node-util/rich-text/lexical-to-plain-text';
import { z } from 'zod';
import { richTextSchema } from '../types/common-types.ts';
import { crmAttachmentInputSchema } from './crm-attachment-dto.ts';

export const crmTaskIntent = z.enum([
    'GENERIC',
    'CALL',
    'CLARIFY_PRICE',
    'QUOTE_FOLLOW_UP',
    'QUOTE_CLARIFY_DELIVERY',
    'QUOTE_CONFIRM',
    'QUOTE_MARK_LOST',
]);
export type CrmTaskIntent = z.infer<typeof crmTaskIntent>;

/**
 * A task is stored with its description as plain text as well, and every write path
 * rejects one that has no text — so the schema is where that becomes visible, both to
 * the API and to the forms that build on it.
 */
export function hasRichTextContent(description: unknown): boolean {
    return !!lexicalToPlainText(description);
}

export const crmTaskInputDtoSchema = z.object({
    description: richTextSchema.refine(hasRichTextContent, {
        message: 'Task description cannot be empty',
    }),
    assignedToId: z.uuid().min(1),
    dueDate: z.date(),
    status: z.enum(CrmTaskStatus),
    taskIntent: crmTaskIntent.optional(),
    links: z.array(
        z.object({
            modelType: z.string().min(1),
            modelId: z.string().min(1),
            title: z.string().nullable(),
        }),
    ),
    attachments: z.array(crmAttachmentInputSchema).optional(),
});

export type CrmTaskInputDTO = z.infer<typeof crmTaskInputDtoSchema>;

export const crmTaskBatchInputDtoSchema = z
    .object({
        shared: crmTaskInputDtoSchema.partial(),
        batch: z.array(crmTaskInputDtoSchema.partial()).min(1),
    })
    .transform(({ shared, batch }) => batch.map((task) => ({ ...shared, ...task })))
    .pipe(z.array(crmTaskInputDtoSchema));
